import React from "react";
import { ArrowRight } from "lucide-react";
import heroImage from "../../assets/analytics-hero.webp";
import { Sparkline } from "./Sparkline";
import { AnimatedValue } from "./AnimatedValue";

function MiniStat({ label, value, series, tone }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-xl border border-white/10 bg-black/40 px-3 py-2.5 backdrop-blur-sm sm:gap-3 sm:px-4 sm:py-3">
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-[0.12em] text-gray-400 sm:text-[11px]">{label}</p>
        <p className="mt-0.5 text-xl font-semibold tabular-nums text-white sm:text-2xl">
          <AnimatedValue value={value} />
        </p>
      </div>
      <Sparkline values={series} tone={tone} height={34} className="w-14 flex-shrink-0 sm:w-20" />
    </div>
  );
}

/**
 * Hero banner for the Analytics dashboard: brand wave visual on the right,
 * headline on the left, live mini-stats + system status stacked on the right.
 */
export function AnalyticsHero({
  liveExecutions,
  successRate,
  execSeries = [],
  successSeries = [],
  healthy = true,
  onExplore,
}) {
  return (
    <section className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#0b0710] lg:min-h-[240px]">
      {/* Brand wave visual — anchored right so the glowing crossover stays in
          view and the plain left edge is what the gradient covers */}
      <img
        src={heroImage}
        alt=""
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-0 h-full w-full object-cover object-right opacity-100 [filter:saturate(1.08)]"
      />
      {/* Left-to-right darkening so the headline stays readable over the art */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0b0710] via-[#0b0710]/55 to-transparent" />
      {/* Gentle bottom fade to seat the banner into the page */}
      <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#0b0710]/70 to-transparent" />

      {/* Titular y métricas van en fila también en móvil. Apilados, el banner
          medía ~450px de alto y empujaba fuera de pantalla justo los números
          que viene a enseñar. Medido a 390 y 430px: la columna de métricas se
          queda en 210px y nada se desborda. */}
      <div className="relative z-10 flex flex-row items-center justify-between gap-3 p-4 sm:gap-6 sm:p-8 lg:justify-between">
        {/* Headline */}
        <div className="min-w-0 max-w-md">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-pink-400 sm:text-[11px]">
            Real-time intelligence
          </p>
          <h2 className="mt-2 text-xl font-bold leading-tight tracking-tight text-white sm:text-2xl md:text-4xl">
            Data that moves.
            <br />
            Decisions that scale.
          </h2>
          <p className="mt-3 max-w-sm text-xs leading-relaxed text-gray-300 sm:text-sm">
            Live analytics to optimize every workflow and empower your operations.
          </p>
          <button
            type="button"
            onClick={onExplore}
            className="mt-4 inline-flex items-center gap-2 rounded-lg border border-pink-500/40 bg-pink-500/10 px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-pink-500/20 sm:mt-5 sm:px-4 sm:text-sm"
          >
            Explore insights
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>

        {/* Live mini-stats + system status */}
        <div className="flex w-[46%] shrink-0 flex-col gap-2 sm:gap-3 lg:w-[300px]">
          <MiniStat label="Live executions" value={liveExecutions} series={execSeries} tone="pink" />
          <MiniStat label="Success rate" value={successRate} series={successSeries} tone="violet" />
          <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-black/40 px-3 py-2 backdrop-blur-sm sm:px-4 sm:py-2.5">
            <span
              className={
                healthy
                  ? "h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]"
                  : "h-2 w-2 rounded-full bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.8)]"
              }
            />
            <span className="truncate text-[11px] text-gray-400 sm:text-xs">System status</span>
            <span
              className={`ml-auto text-[11px] font-medium sm:text-xs ${healthy ? "text-emerald-300" : "text-amber-300"}`}
            >
              {healthy ? "Healthy" : "Degraded"}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

export default AnalyticsHero;
